import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, DatePicker, Space, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import Api from '../../Network';
import MobileTable from '../../Component/MobileTable';
import NameByLang from '../../Component/nameByLang';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface Props {
  enabled: boolean;
  driverId: string;
}

interface Row {
  sales_representative_id: number;
  sales_representative_name: string;
  product_id: number;
  product_name_en: string | null;
  product_name_ar: string | null;
  quantity_sold: number;
  avg_price_including_tax: number;
}

interface ResponseData {
  date_range: { start_date: string; end_date: string };
  total_sales_in_period: number;
  rows: Row[];
}

const InvoiceLineItemsAnalyticsTab: React.FC<Props> = ({ enabled, driverId }) => {
  const { t, i18n } = useTranslation();
  const [rangeDraft, setRangeDraft] = useState<[Dayjs, Dayjs] | null>(null);
  const [rangeApplied, setRangeApplied] = useState<[Dayjs, Dayjs] | null>(null);
  const syncedDraftFromResponse = useRef(false);

  const params = useMemo(() => {
    const base: Record<string, string> = { driver_id: driverId };
    if (rangeApplied) {
      base.start_date = rangeApplied[0].format('YYYY-MM-DD');
      base.end_date = rangeApplied[1].format('YYYY-MM-DD');
    }
    return base;
  }, [rangeApplied, driverId]);

  const queryRangeKey = rangeApplied
    ? `${rangeApplied[0].format('YYYY-MM-DD')}_${rangeApplied[1].format('YYYY-MM-DD')}`
    : 'default';

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sales-analytics-invoice-line-items', driverId, queryRangeKey],
    queryFn: async (): Promise<ResponseData> => {
      const res = await Api.get<{ status?: boolean; data?: ResponseData }>(
        'api/admin/dashboard/sales-analytics-invoice-line-items',
        { params }
      );
      if (!res?.status || !res.data) {
        throw new Error('Failed to load invoice line-item analytics');
      }
      return res.data;
    },
    enabled: enabled && !!driverId,
  });

  useEffect(() => {
    if (!data?.date_range || syncedDraftFromResponse.current) {
      return;
    }
    setRangeDraft([dayjs(data.date_range.start_date), dayjs(data.date_range.end_date)]);
    syncedDraftFromResponse.current = true;
  }, [data?.date_range]);

  const applyRange = () => {
    if (rangeDraft?.[0] && rangeDraft[1]) {
      setRangeApplied([rangeDraft[0], rangeDraft[1]]);
    } else {
      setRangeApplied(null);
    }
  };

  const columns = [
    {
      title: t('salesRepresentatives.salesAnalytics.columns.product', 'Product'),
      key: 'product',
      render: (_: unknown, r: Row) => (
        <NameByLang
          name_en={r.product_name_en ?? undefined}
          name_ar={r.product_name_ar ?? undefined}
        />
      ),
    },
    {
      title: t('salesRepresentatives.salesAnalytics.columns.quantity', 'Quantity sold'),
      dataIndex: 'quantity_sold',
      key: 'quantity_sold',
      align: 'right' as const,
      render: (v: number) => Number(v).toLocaleString(i18n.language),
    },
    {
      title: t('salesRepresentatives.salesAnalytics.columns.avgPriceTax', 'Avg. price (incl. tax)'),
      dataIndex: 'avg_price_including_tax',
      key: 'avg_price_including_tax',
      align: 'right' as const,
      render: (v: number) => `${Number(v).toLocaleString(i18n.language)} ${t('common.SAR', 'SAR')}`,
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>{t('salesRepresentatives.salesOverview.dateRange', 'Date range')}</Text>
          <RangePicker
            value={rangeDraft}
            onChange={(d) => setRangeDraft(d?.[0] && d[1] ? [d[0], d[1]] : null)}
          />
          <Button type="primary" onClick={applyRange}>
            {t('salesRepresentatives.salesAnalytics.applyDateRange', 'Apply range')}
          </Button>
        </Space>
      </Card>

      <Card loading={isLoading || isFetching}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t(
            'salesRepresentatives.invoiceLineItemsAnalytics.description',
            'Calculated from invoice line items only, not orders.'
          )}
        </Text>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>
          {t('salesRepresentatives.invoiceLineItemsAnalytics.total', 'Total from invoice items')}{' '}
          {Number(data?.total_sales_in_period || 0).toLocaleString(i18n.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          {t('common.SAR', 'SAR')}
        </Text>
        <MobileTable
          rowKey={(r: Row) => `${r.sales_representative_id}-${r.product_id}`}
          columns={columns}
          dataSource={data?.rows || []}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 700 }}
        />
      </Card>
    </div>
  );
};

export default InvoiceLineItemsAnalyticsTab;
